import React, { useEffect, useState } from 'react';
import { customStyle } from '@custom/Utils/utils';
import { Field, Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import { Loader, SuccessModal } from '@ohif/ui';
import { useTheme } from "../../ui/src/contextProviders"
import { clearGeneralSettings } from '@custom/store/slices/billing/getCommonGeneralSettings';
import addBillingGeneralSettings from '@custom/rtk-apis/billing/addBillingGeneralSettings';
import { clearHospitalBillingGeneralSettings } from '@custom/store/slices/billing/addBillingGeneralSettings';
import getCommonGeneralSettings from '@custom/rtk-apis/billing/getCommonGeneralSettings';
import getBillingGeneralSettings from '@custom/rtk-apis/billing/getGeneralSettings';

const taxMapping = {
    INR: "GST",
    USD: "VAT",
    EUR: "VAT",
    AED: "VAT",
};

const GeneralSettings = () => {
    const { isDarkMode } = useTheme();
    const [generalSettings, setGeneralSettings] = useState<any>({});
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [displayMessage, setDisplayMessage] = useState('');
    const hospitalInitial = localStorage.getItem('hospitalName') || sessionStorage.getItem('hospitalName') || '';

    const dispatch = useDispatch<AppDispatch>();
    const commonGeneralSettingsData = useSelector((state: RootState) => state.getCommonGeneralSettingsResponse);
    const saveGeneralSettingsData = useSelector((state: RootState) => state.addBillingGeneralSettingsResponse);
    const billingGeneralSettingsData = useSelector((state: RootState) => state.getBillingGeneralSettingsSliceResponse);

    const submitForm = (values: any) => {
        const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
        const invoicePart1Value = getDynamicInvoiceValue(values.invoicePart1);
        const invoicePart2Value = getDynamicInvoiceValue(values.invoicePart2);
        const invoicePart3Value = values.lastSeriesNo;

        const numberFormat = [invoicePart1Value, invoicePart2Value, invoicePart3Value]
            .filter(Boolean)
            .join('-');

        const payload = {
            hospitalId: hospitalId,
            currency: values.currency,
            taxConfig: values.taxConfig,
            urgency: values.urgency,
            urgencyCharges: values.urgencyCharges,
            taxPercentage: values.taxPercentage,
            gstNo: values.gstNo,
            numberFormat: numberFormat,
            lastSeriesNo: invoicePart3Value
        };
        dispatch(addBillingGeneralSettings(payload));
    };

    const invoiceOptions = [
        { label: "Hospital Initial", value: "hospitalInitial" },
        { label: "Serial No.", value: "serialNo" },
        { label: "Date (DDMMYY)", value: "ddmmyy" },
        { label: "Date (DDMMYYYY)", value: "ddmmyyyy" },
        { label: "Year (YYYY)", value: "yyyy" },
    ];

    useEffect(() => {
        dispatch(getCommonGeneralSettings());
        dispatch(getBillingGeneralSettings());
    }, [dispatch]);

    useEffect(() => {
        if (commonGeneralSettingsData?.status === 'success') {
            setGeneralSettings(commonGeneralSettingsData?.result);
        }
        return () => {
            dispatch(clearGeneralSettings());
        };
    }, [commonGeneralSettingsData.status]);

    useEffect(() => {
        if (billingGeneralSettingsData?.status === 'success') {
            console.log("");
        }
        return () => {
            dispatch(clearGeneralSettings());
        };
    }, [billingGeneralSettingsData.status]);

    useEffect(() => {
        if (saveGeneralSettingsData?.status === 'success') {
            setIsSuccessModalOpen(true);
            setDisplayMessage(saveGeneralSettingsData.msg);
        }
        return () => {
            dispatch(getBillingGeneralSettings());
            dispatch(clearHospitalBillingGeneralSettings());
        };
    }, [saveGeneralSettingsData.status]);

    const getInitials = (name: string) => {
        if (!name) return '';
        const nameParts = name.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts[1] || '';
        const firstInitial = firstName.charAt(0).toUpperCase();
        const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
        return firstInitial + lastInitial;
    };

    const getDynamicInvoiceValue = (part: string) => {
        const now = new Date();
        switch (part) {
            case "hospitalInitial":
                return getInitials(hospitalInitial);
            case "serialNo":
                return '0000';
            case "ddmmyy":
                return now.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }).replace(/\//g, '');
            case "ddmmyyyy":
                return now.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '');
            case "yyyy":
                return now.getFullYear().toString();
            default:
                return "";
        }
    };

    const inferInvoicePartType = (part: string, index: number) => {
        if (!part) return "";
        const hospitalInitialValue = getInitials(hospitalInitial);
        const now = new Date();
        const ddmmyy = now.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }).replace(/\//g, '');
        const ddmmyyyy = now.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '');
        const yyyy = now.getFullYear().toString();

        if (part === hospitalInitialValue) return "hospitalInitial";
        if (part === ddmmyy) return "ddmmyy";
        if (part === ddmmyyyy) return "ddmmyyyy";
        if (part === yyyy) return "yyyy";
        if (/^\d{6}$/.test(part)) return "ddmmyy";
        if (/^\d{8}$/.test(part)) return "ddmmyyyy";
        if (/^\d{4}$/.test(part)) return "yyyy";
        if (/^\d+$/.test(part)) return "serialNo";
        return "hospitalInitial";
    };

    const validate = (values: any) => {
        const errors: any = {};
        if (values.gstNo && !/^[a-zA-Z0-9]{15}$/.test(values.gstNo)) {
            errors.gstNo = 'GST Number must contain 15 characters';
        }
        if (!values.lastSeriesNo) {
            errors.lastSeriesNo = 'Series Number is required';
        }
        return errors;
    };

    const getInitialInvoiceParts = () => {
        const numberFormat = billingGeneralSettingsData?.result?.numberFormat || "";
        const parts = numberFormat.split('-');
        return {
            invoicePart1: inferInvoicePartType(parts[0], 0),
            invoicePart2: inferInvoicePartType(parts[1], 1),
            invoicePart3: "serialNo",
            lastSeriesNo: billingGeneralSettingsData?.result?.lastSeriesNo || '0000'
        };
    };

    return (
        <div className='page__body'>
            <div className='billing details'>
            <div className='form form--innersec'>
                    <Formik
                        enableReinitialize
                        initialValues={{
                            currency: billingGeneralSettingsData?.result?.currency || commonGeneralSettingsData?.result?.currency || '',
                            taxConfig: billingGeneralSettingsData?.result?.taxConfig || commonGeneralSettingsData?.result?.taxConfig || '',
                            urgency: billingGeneralSettingsData?.result?.urgency || commonGeneralSettingsData?.result?.urgency || '',
                            urgencyCharges: billingGeneralSettingsData?.result?.urgencyCharges || '',
                            taxPercentage: billingGeneralSettingsData?.result?.taxPercentage || '',
                            gstNo: billingGeneralSettingsData?.result?.gstNo || '',
                            ...getInitialInvoiceParts(),
                        }}
                        validate={validate}
                        onSubmit={submitForm}
                    >
                        {({
                            values,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue,
                            touched,
                            errors,
                        }) => {
                            useEffect(() => {
                                if (values.currency) {
                                    setFieldValue("taxConfig", taxMapping[values.currency] || "VAT");
                                } else {
                                    setFieldValue("taxConfig", "");
                                }
                            }, [values.currency, setFieldValue]);

                            const selectedValues = [values.invoicePart1, values.invoicePart2, values.invoicePart3].filter(Boolean);
                            const getFilteredOptions = (currentField: string, selected: string) => {
                                return invoiceOptions.map((opt) => {
                                    const isSelectedInOtherFields = selectedValues.includes(opt.value) && opt.value !== selected;
                                    const isSerialNoDisabled = opt.value === "serialNo" && currentField !== "invoicePart3";
                                    return {
                                        ...opt,
                                        disabled: isSelectedInOtherFields || isSerialNoDisabled,
                                    };
                                });
                            };

                            const handleResetInvoice = () => {
                                setFieldValue("invoicePart2", "");
                                setFieldValue("invoicePart3", "");
                                setFieldValue("lastSeriesNo", "");
                            };

                            return (
                                <Form>
                                    <div className='form__section'>
                                        <h2 className='form__section__title'>General Settings</h2>
                                        <div className='formRow formRow__4'>
                                            <div className="formCol">
                                                <label className="form__label" htmlFor="currency">Currency</label>
                                                <select
                                                    id="currency"
                                                    name="currency"
                                                    className="form__select form__select--sm"
                                                    onChange={(e) => {
                                                        setFieldValue("currency", e.target.value);
                                                    }}
                                                    onBlur={handleBlur}
                                                    value={values.currency}
                                                    autoComplete='off'
                                                    style={customStyle.select}
                                                >
                                                    <option value="">-Select-</option>
                                                    {generalSettings?.currency?.map((currency) => (
                                                        <option key={currency} value={currency}>{currency}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="formCol">
                                                <label className="form__label" htmlFor="taxConfig">Tax Configuration</label>
                                                <input
                                                    id="taxConfig"
                                                    name="taxConfig"
                                                    type="text"
                                                    className="form__select form__select--sm"
                                                    style={{ cursor: 'not-allowed', color: isDarkMode ? '#fff' : '#333', backgroundColor: isDarkMode ? '#2d3748' : '#e8e6e5' }}
                                                    onBlur={handleBlur}
                                                    value={values.taxConfig || "-Select Currency First-"}
                                                    autoComplete="off"
                                                    disabled
                                                    readOnly
                                                />
                                            </div>
                                            <div className="formCol">
                                                <label className="form__label" htmlFor="taxPercentage">
                                                    {values.taxConfig ? `${values.taxConfig} %` : 'Tax %'}
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form__input form__input--sm"
                                                    id="taxPercentage"
                                                    name="taxPercentage"
                                                    placeholder={values.taxConfig ? `${values.taxConfig} %` : 'Tax %'}
                                                    value={values.taxPercentage ? values.taxPercentage + '%' : ''}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace('%', '');
                                                        handleChange({ target: { name: 'taxPercentage', value } });
                                                    }}
                                                    onBlur={handleBlur}
                                                    autoComplete="off"
                                                    disabled={!values.currency}
                                                    style={!values.currency ? { cursor: 'not-allowed', color: isDarkMode ? '#fff' : '#333', backgroundColor: isDarkMode ? '#2d3748' : '#e8e6e5' } : {}}
                                                />
                                            </div>
                                            <div className="formCol">
                                                <label className="form__label" htmlFor="gstNo">
                                                    {values.taxConfig ? `${values.taxConfig} Number` : 'Tax Number'}
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form__input form__input--sm"
                                                    id="gstNo"
                                                    name="gstNo"
                                                    placeholder={values.taxConfig ? `${values.taxConfig} Number` : 'Tax Number'}
                                                    value={values.gstNo}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    autoComplete="off"
                                                    maxLength={15}
                                                    disabled={!values.currency}
                                                    style={!values.currency ? { cursor: 'not-allowed', color: isDarkMode ? '#fff' : '#333', backgroundColor: isDarkMode ? '#2d3748' : '#e8e6e5' } : {}}
                                                />
                                                {errors.gstNo && touched.gstNo && (
                                                    <span className="text-red-500 text-sm font-medium
                                                ">{errors.gstNo}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                   <div className='form__section'>
                                       <h2 className='form__section__title'>Invoice</h2>
                                        <div className="formCol">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <label className="form__label" htmlFor='invoicePart1'>Invoice Number Format</label>
                                                {(values.invoicePart1 || values.invoicePart2 || values.invoicePart3 || values.lastSeriesNo !== '0000') && (
                                                    <button
                                                        type="button"
                                                        className='text-lg text-red-600 ml-2'
                                                        style={{ marginTop: '-7px' }}
                                                        onClick={handleResetInvoice}
                                                    >
                                                        Reset Invoice
                                                    </button>
                                                )}
                                            </div>
                                            <label className="form__label-billing" style={{ color: isDarkMode ? '#fff' : '#333' }} htmlFor='invoicePart3'>eg: DS2401202500001</label>
                                            <div className='formRow formRow__3' style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                                <div className='formCol'>
                                                    <div className='form__Field'>
                                                        <select
                                                            id="invoicePart1"
                                                            name="invoicePart1"
                                                            className={`form__select form__select--sm`}
                                                            onChange={(e) => { handleChange(e); }}
                                                            onBlur={handleBlur}
                                                            value={values.invoicePart1}
                                                            autoComplete='off'
                                                            style={{
                                                                color: isDarkMode ? '#fff' : '#333',
                                                                cursor: 'not-allowed',
                                                                backgroundColor: isDarkMode ? '#2d3748' : '#e8e6e5',
                                                                opacity: 1
                                                            }}
                                                            disabled
                                                        >
                                                            <option value="hospitalInitial" style={{ color: isDarkMode ? '#fff' : '#333' }}>Hospital Initial</option>
                                                        </select>
                                                        <input
                                                            type="text"
                                                            className="form__select form__select--sm mt-6"
                                                            style={{
                                                                cursor: 'not-allowed',
                                                                color: isDarkMode ? '#fff' : '#333',
                                                                backgroundColor: isDarkMode ? '#2d3748' : '#e8e6e5',
                                                            }}
                                                            value={hospitalInitial ? getInitials(hospitalInitial) : ''}
                                                            disabled
                                                            readOnly
                                                        />
                                                    </div>
                                                </div>
                                                <span style={{ paddingTop: '8px' }}>-</span>

                                                <div className='formCol'>
                                                    <div className='form__Field'>
                                                        <select
                                                            id="invoicePart2"
                                                            name="invoicePart2"
                                                            className={`form__select form__select--sm`}
                                                            onChange={(e) => { handleChange(e); }}
                                                            onBlur={handleBlur}
                                                            value={values.invoicePart2}
                                                            autoComplete='off'
                                                            style={customStyle.select}
                                                        >
                                                            <option value="" style={{ color: isDarkMode ? '#fff' : '#333' }}>-Select-</option>
                                                            {getFilteredOptions('invoicePart2', values.invoicePart2).map((opt) => (
                                                                <option
                                                                    key={opt.value}
                                                                    value={opt.value}
                                                                    disabled={opt.disabled}
                                                                    style={opt.disabled ? { backgroundColor: '#e7e7e7', color: isDarkMode ? '#fff' : '#333', cursor: 'not-allowed' } : {}}
                                                                >
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <input
                                                            type="text"
                                                            className="form__select form__select--sm mt-6"
                                                            style={{
                                                                cursor: 'not-allowed',
                                                                color: isDarkMode ? '#fff' : '#333',
                                                                backgroundColor: isDarkMode ? '#2d3748' : '#e8e6e5'
                                                            }}
                                                            value={getDynamicInvoiceValue(values.invoicePart2)}
                                                            disabled
                                                            readOnly
                                                        />
                                                    </div>
                                                </div>
                                                <span style={{ paddingTop: '8px' }}>-</span>

                                                <div className='formCol'>
                                                    <div className='form__Field'>
                                                        <select
                                                            id="invoicePart3"
                                                            name="invoicePart3"
                                                            className={`form__select form__select--sm`}
                                                            onChange={(e) => { handleChange(e); }}
                                                            onBlur={handleBlur}
                                                            value={values.invoicePart3}
                                                            autoComplete='off'
                                                            style={{
                                                                color: isDarkMode ? '#fff' : '#333',
                                                                cursor: 'not-allowed',
                                                                backgroundColor: isDarkMode ? '#2d3748' : '#e8e6e5',
                                                                opacity: 1
                                                            }}
                                                            disabled
                                                        >
                                                            <option value="serialNo" style={{ color: isDarkMode ? '#fff' : '#333' }}>Serial No.</option>
                                                        </select>
                                                        <Field
                                                            name="lastSeriesNo"
                                                            render={({ field, form: { setFieldValue } }: { field: any, form: any }) => (
                                                                <input
                                                                    type="text"
                                                                    className="form__select form__select--sm mt-6"
                                                                    {...field}
                                                                    placeholder="Enter series number"
                                                                    maxLength={5}
                                                                    onChange={(e) => {
                                                                        setFieldValue('lastSeriesNo', e.target.value);
                                                                    }}
                                                                    disabled={field.value !== ""}
                                                                    style={{
                                                                        ...(field.value !== "" ? {
                                                                            cursor: 'not-allowed',
                                                                            backgroundColor: isDarkMode ? '#2d3748' : '#e8e6e5',
                                                                            color: isDarkMode ? '#fff' : '#333',
                                                                            opacity: 1
                                                                        } : {
                                                                            cursor: 'text',
                                                                            backgroundColor: isDarkMode ? '#2d3748' : '#e8e6e5',
                                                                            color: isDarkMode ? '#fff' : '#333'
                                                                        })
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                        {touched.lastSeriesNo && errors.lastSeriesNo && (
                                                            <span className="text-red-500 text-sm font-medium">
                                                                {errors.lastSeriesNo}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='btn__group mt-4'>
                                            <button className='btn btn__primary' type='submit'>
                                                Save
                                            </button>
                                        </div>
                                   </div>
                                </Form>
                            );
                        }}
                    </Formik>
            </div>
            <SuccessModal
                isSuccessModalOpen={isSuccessModalOpen}
                title={displayMessage}
                handleCloseModal={() => setIsSuccessModalOpen(false)}
            />
            <Loader isLoaderOpen={commonGeneralSettingsData.loading || saveGeneralSettingsData.loading} />
            </div>
        </div>

    );
};

export default GeneralSettings;
